import React, { useState, useMemo, useEffect } from 'react';
import cn from 'classnames';
import MailchimpSubscribe from 'react-mailchimp-subscribe';

import { validateEmail } from '../util/functions';
import { useSiteMetaData } from '../util/queries/site';

import Card from './Card';
import Button from './Button';

import CheckIcon from './icons/Check';

import * as styles from './SubscribeForm.module.css';
import { useSettings } from '../util/queries/settings';
import { externalLink } from '../util';

const SUBSCRIBED = 'btg_subscribed';

const isSubscribed = () => {
  return window.localStorage.getItem(SUBSCRIBED) === 'true';
};

const setSubscribed = () => {
  try {
    window.localStorage.setItem(SUBSCRIBED, 'true');
  } catch (e) {
    console.error(e);
  }
};

const FormMessage = ({ icon, label, children }) => (
  <div className={cn(styles.formMessage)}>
    <div className={cn(styles.formMessageIcon)}>{icon}</div>
    <div>
      <p>
        <strong>{label}</strong>
      </p>
      {children}
    </div>
  </div>
);

const MailchimpForm = ({
  status,
  onValidated,
  fallbackUrl,
  validationError,
  inputLabel,
  cta,
  successTitle,
  successText,
  errorTitle,
  errorText,
  onSuccess
}) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const isValid = useMemo(
    () => email.length > 0 && validateEmail(email),
    [email]
  );

  useEffect(() => {
    if (status === 'success') {
      onSuccess();
    }
  }, [status, onSuccess]);

  const submit = () => {
    if (!isValid) {
      setMessage(validationError);
      return;
    }

    setMessage('');

    onValidated({
      EMAIL: email
    });
  };

  return (
    <div>
      {(!status || status === 'sending') && (
        <>
          <div className={cn(styles.formStatus)}>
            {status === 'sending' ? 'Cargando...' : message}
          </div>
          <div className={styles.form}>
            <input
              className={cn(styles.emailInput)}
              type="email"
              aria-label="Email"
              autoComplete="email"
              name="email"
              placeholder={inputLabel}
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && submit()}
            />

            <Button
              className={cn(styles.submitButton)}
              onClick={submit}
              label={cta}
              disabled={status === 'sending'}
              inverted
            />
          </div>
        </>
      )}

      {status === 'success' && (
        <FormMessage icon={<CheckIcon width={24} />} label={successTitle}>
          <p>{successText}</p>
        </FormMessage>
      )}

      {status === 'error' && (
        <FormMessage icon={<CheckIcon />} label={errorTitle}>
          <p>{errorText}</p>
          <Button href={fallbackUrl} {...externalLink}>
            ir
          </Button>
        </FormMessage>
      )}
    </div>
  );
};

const SubscribeForm = ({ id }) => {
  const { subscribe } = useSettings();
  const [showForm, setShowForm] = useState();
  useEffect(() => {
    setShowForm(!isSubscribed);
  }, []);

  const onSuccess = () => {
    setSubscribed();
    setTimeout(() => {
      setShowForm(false);
    }, 3000);
  };
  const { subscribe: subscribeData } = useSettings();
  const { mailchimpUrl, mailchimpFallbackUrl } = useSiteMetaData();

  if (!subscribe.show) return null;
  return (
    <Card id={id} animate className={cn(styles.root)} title=" ">
      {showForm ? (
        <div className={cn(styles.wrapper)}>
          <div className={cn(styles.textBlock)}>
            <h2 className={cn(styles.headline)}>{subscribeData.title}</h2>
          </div>
          <div className={cn(styles.formBlock)}>
            <MailchimpSubscribe
              url={subscribeData.url || mailchimpUrl}
              render={({ subscribe, status }) => (
                <MailchimpForm
                  status={status}
                  onValidated={subscribe}
                  onSuccess={onSuccess}
                  fallbackUrl={
                    subscribeData.fallbackUrl || mailchimpFallbackUrl
                  }
                  {...subscribeData}
                />
              )}
            />
          </div>
        </div>
      ) : (
        <div className={styles.expandWrapper}>
          <Button
            label={subscribeData.cta}
            onClick={() => {
              setShowForm(true);
            }}
          ></Button>
        </div>
      )}
    </Card>
  );
};

export default SubscribeForm;
